import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { invoiceApi, GetInvoicesParams } from '../services/invoices'
import { Invoice, InvoiceCreate, InvoiceStatus, InvoiceUpdate, CorrectionInvoiceCreate } from '../types/invoice'
import { settingsKeys } from './useSettings'
import { clientKeys } from './useClients'
import { productKeys } from './useProducts'

// Query keys
export const invoiceKeys = {
  all: ['invoices'] as const,
  lists: () => [...invoiceKeys.all, 'list'] as const,
  list: (params?: GetInvoicesParams) => [...invoiceKeys.lists(), params] as const,
  details: () => [...invoiceKeys.all, 'detail'] as const,
  detail: (id: number) => [...invoiceKeys.details(), id] as const,
}

// Hooks
export function useInvoicesList(params?: GetInvoicesParams) {
  return useQuery({
    queryKey: invoiceKeys.list(params),
    queryFn: () => invoiceApi.getAll(params),
  })
}

export function useInvoice(id: number) {
  return useQuery({
    queryKey: invoiceKeys.detail(id),
    queryFn: () => invoiceApi.getById(id),
    enabled: !!id,
  })
}

export function useCreateInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: InvoiceCreate) => invoiceApi.create(data),
    onSuccess: () => {
      // Invalidate invoices
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() })
      // Invalidate auto-saved entities (clients, sender profiles, bank accounts)
      queryClient.invalidateQueries({ queryKey: clientKeys.all })
      queryClient.invalidateQueries({ queryKey: settingsKeys.senderProfiles() })
      queryClient.invalidateQueries({ queryKey: settingsKeys.bankAccounts() })
      // Invalidate auto-saved products
      queryClient.invalidateQueries({ queryKey: productKeys.lists() })
    },
  })
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: InvoiceUpdate }) =>
      invoiceApi.update(id, data),
    onSuccess: (updatedInvoice: Invoice) => {
      queryClient.setQueryData(invoiceKeys.detail(updatedInvoice.id), updatedInvoice)
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() })
    },
  })
}

export function useUpdateInvoiceStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: InvoiceStatus }) =>
      invoiceApi.updateStatus(id, status),
    onSuccess: (updatedInvoice: Invoice) => {
      queryClient.setQueryData(invoiceKeys.detail(updatedInvoice.id), updatedInvoice)
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() })
    },
  })
}

export function useAnnulInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => invoiceApi.annul(id),
    onSuccess: (updatedInvoice: Invoice) => {
      queryClient.setQueryData(invoiceKeys.detail(updatedInvoice.id), updatedInvoice)
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() })
    },
  })
}

export function useCreateCorrectionInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: CorrectionInvoiceCreate }) =>
      invoiceApi.createCorrection(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() })
    },
  })
}

export function useDeleteInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => invoiceApi.delete(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: invoiceKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() })
    },
  })
}

// Utility hook for getting next invoice number
export function useNextInvoiceNumber(prefix?: string) {
  const { data: invoices } = useInvoicesList()

  const getNextInvoiceNumber = useCallback((): string => {
    const year = new Date().getFullYear()
    const invoicePrefix = prefix || ''
    const basePrefix = invoicePrefix ? `${invoicePrefix}-${year}` : `${year}`

    let maxNumber = 0
    for (const inv of invoices || []) {
      if (
        inv.invoiceNumber.startsWith(`${basePrefix}-`) ||
        inv.invoiceNumber.startsWith(`${year}-`)
      ) {
        const suffix = Number(inv.invoiceNumber.split('-').pop())
        if (Number.isInteger(suffix) && suffix > maxNumber) maxNumber = suffix
      }
    }
    return `${basePrefix}-${String(maxNumber + 1).padStart(4, '0')}`
  }, [invoices, prefix])

  return { getNextInvoiceNumber }
}
